import React from 'react';
import { Calculator, Trash2, BarChart3 } from 'lucide-react';
import { PickerSelect } from '../ui/PickerSelect';
import { cn } from '../ui/Table';
import { Button } from '../ui/arc/button';
import { Badge } from '../ui/arc/badge';
import { Input } from '../ui/arc/input';
import { EmptyState } from '../ui/arc/empty-state';

// Calculadora de rentabilidad (pestaña Costos & BI → Calculadora) — rediseño 10/10/2026.
// SOLO UI: el cálculo sigue siendo `calculateProduct` de Costs.tsx (se recibe por props) y
// los resultados son los mismos con los mismos inputs. Patrón visual de src/Insumos.tsx.

export interface CalcMaterial {
    id: number; name: string; type: 'simple' | 'acrylic'; unit_cost: number; currency: 'ARS' | 'USD';
}
export interface CalcItem { id: number; quantity: number; width_mm?: number; height_mm?: number }

const CTL = 'h-9 w-full min-w-0 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-2.5 text-[length:var(--text-sm)] text-ink tabular-nums placeholder:text-faint focus:border-[var(--foreground)] focus:outline-none';

export function CalcPanel({
    materials, items, setItems, productName, setProductName, markup, setMarkup, result, onCalculate, fmtARS,
    usdRate, ivaPercent, wastePercent,
}: {
    materials: CalcMaterial[];
    items: CalcItem[];
    setItems: (v: CalcItem[]) => void;
    productName: string;
    setProductName: (v: string) => void;
    markup: number;
    setMarkup: (v: number) => void;
    result: any;
    onCalculate: () => void;
    fmtARS: (n: number) => string;
    usdRate?: number;
    ivaPercent?: number;
    wastePercent?: number;
}) {
    const upd = (idx: number, patch: Partial<CalcItem>) => {
        const n = [...items];
        n[idx] = { ...n[idx], ...patch };
        setItems(n);
    };
    const add = (v: string | number | null) => {
        const id = Number(v);
        if (!id) return;
        const mat = materials.find(m => m.id === id);
        setItems([...items, {
            id, quantity: 1,
            width_mm: mat?.type === 'acrylic' ? 100 : undefined,
            height_mm: mat?.type === 'acrylic' ? 100 : undefined,
        }]);
    };
    const money = (n: number) => '$' + fmtARS(n);
    const contrib = result ? result.pvp - result.totalVariableCost : 0;

    return (
        <div className="tw w-full min-w-0 pb-12">
            <header className="mb-5">
                <h2 className="text-[length:var(--text-xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Calculadora de rentabilidad</h2>
                <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
                    Armá un producto con sus insumos, poné el markup y calculá el precio sugerido, el ROAS de equilibrio y cuántas ventas cubren los costos fijos.
                </p>
            </header>

            <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
                {/* ── Composición ── */}
                <section className="grid gap-5 rounded-[var(--radius-surface)] border border-line bg-card p-5">
                    <Input label="Nombre del producto" placeholder="Ej: Porta retrato 20×30" value={productName}
                        onChange={e => setProductName(e.target.value)} />

                    <div>
                        <span className="mb-2 block text-[length:var(--text-sm)] font-medium text-ink">Insumos</span>
                        <PickerSelect placeholder="Buscá y agregá un insumo…" value={null} onChange={add}
                            options={materials.map(m => ({
                                value: m.id, label: m.name,
                                sublabel: m.type === 'acrylic' ? `Acrílico · ${m.currency} ${m.unit_cost}/kg` : `${m.currency} ${m.unit_cost} c/u`,
                            }))} />

                        {items.length === 0 ? (
                            <div className="mt-3 rounded-[var(--radius-control)] border border-dashed border-line px-4 py-6 text-center text-[length:var(--text-sm)] text-faint">
                                Todavía no agregaste insumos.
                            </div>
                        ) : (
                            <ul className="m-0 mt-3 flex list-none flex-col gap-2 p-0">
                                {items.map((it, idx) => {
                                    const mat = materials.find(m => m.id === it.id);
                                    if (!mat) return null;
                                    return (
                                        <li key={idx} className="flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-line p-2.5">
                                            <div className="flex min-w-[10rem] flex-1 items-center gap-2">
                                                <span className="truncate text-[length:var(--text-sm)] font-medium text-ink">{mat.name}</span>
                                                <Badge size="sm" tone={mat.type === 'acrylic' ? 'info' : 'neutral'}>{mat.type === 'acrylic' ? 'Acrílico (kg)' : 'Por unidad'}</Badge>
                                            </div>
                                            {mat.type === 'acrylic' && (
                                                <div className="flex items-center gap-1.5" title="Medida de la pieza; el costo sale del precio por kg de la placa">
                                                    <input type="number" aria-label="Ancho (mm)" placeholder="Ancho" value={it.width_mm || ''}
                                                        onChange={e => upd(idx, { width_mm: Number(e.target.value) })} className={cn(CTL, 'w-20 text-right')} />
                                                    <span className="text-faint">×</span>
                                                    <input type="number" aria-label="Alto (mm)" placeholder="Alto" value={it.height_mm || ''}
                                                        onChange={e => upd(idx, { height_mm: Number(e.target.value) })} className={cn(CTL, 'w-20 text-right')} />
                                                    <span className="text-[length:var(--text-xs)] text-faint">mm</span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[length:var(--text-xs)] text-faint">Cant.</span>
                                                <input type="number" min={1} aria-label="Cantidad" value={it.quantity}
                                                    onChange={e => upd(idx, { quantity: Number(e.target.value) })} className={cn(CTL, 'w-16 text-center')} />
                                            </div>
                                            <Button variant="ghost" size="sm" aria-label={`Quitar ${mat.name}`} title="Quitar"
                                                onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 size={14} /></Button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div>
                        <div className="mb-2 flex items-baseline justify-between">
                            <span className="text-[length:var(--text-sm)] font-medium text-ink">Markup deseado</span>
                            <span className="text-[length:var(--text-xs)] text-faint">sobre el costo variable</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <input type="range" min={0} max={200} value={markup} aria-label="Markup"
                                onChange={e => setMarkup(Number(e.target.value))}
                                className="h-2 min-w-0 flex-1 cursor-pointer accent-[var(--foreground)]" />
                            <div className="flex items-center gap-1">
                                <input type="number" aria-label="Markup (%)" value={markup} onChange={e => setMarkup(Number(e.target.value))}
                                    className={cn(CTL, 'w-20 text-right')} />
                                <span className="text-[length:var(--text-sm)] text-faint">%</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
                        <p className="text-[length:var(--text-xs)] text-faint">
                            Usa: USD {usdRate ?? '—'} · IVA {ivaPercent ?? '—'}% · desperdicio acrílico {wastePercent ?? '—'}% (se cambian en Config)
                        </p>
                        <Button onClick={onCalculate} disabled={items.length === 0}>
                            <BarChart3 size={16} /> Calcular
                        </Button>
                    </div>
                </section>

                {/* ── Resultado ── */}
                <aside className="min-w-0 lg:sticky lg:top-4">
                    <section className="rounded-[var(--radius-surface)] border border-line bg-card p-5">
                        <h3 className="mb-3 text-[length:var(--text-base)] font-semibold text-ink">Resultado{productName ? ` · ${productName}` : ''}</h3>
                        {!result ? (
                            <EmptyState icon={<Calculator width={22} height={22} strokeWidth={1.5} />}
                                title="Sin cálculo todavía" description="Agregá insumos y tocá Calcular." />
                        ) : (
                            <div className="grid gap-4">
                                <div className="rounded-[var(--radius-panel)] bg-muted p-4">
                                    <div className="text-[length:var(--text-xs)] uppercase tracking-wide text-faint">PVP final (con IVA)</div>
                                    <div className="mt-1 text-[length:var(--text-3xl)] font-semibold leading-tight tracking-[var(--tracking-display)] tabular-nums text-ink">{money(result.pvpWithTax)}</div>
                                    <div className="mt-1 text-[length:var(--text-sm)] tabular-nums text-soft">Sin IVA: {money(result.pvp)}</div>
                                </div>
                                <dl className="m-0 grid gap-1.5 text-[length:var(--text-sm)]">
                                    {[
                                        ['Costo variable', money(result.totalVariableCost)],
                                        ['Contribución por unidad', money(contrib)],
                                        ['Costos fijos / mes', money(result.totalFixedCosts)],
                                    ].map(([k, v]) => (
                                        <div key={k} className="flex items-baseline justify-between gap-3">
                                            <dt className="text-soft">{k}</dt><dd className="m-0 tabular-nums text-ink">{v}</dd>
                                        </div>
                                    ))}
                                </dl>
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="rounded-[var(--radius-control)] border border-line p-3">
                                        <div className="text-[length:var(--text-xs)] text-faint">ROAS de equilibrio</div>
                                        <div className="text-[length:var(--text-xl)] font-semibold tabular-nums text-ink">{result.roasBreakEven.toFixed(2)}×</div>
                                        <div className="text-[length:var(--text-xs)] text-faint">retorno mínimo en ads</div>
                                    </div>
                                    <div className="rounded-[var(--radius-control)] border border-line p-3">
                                        <div className="text-[length:var(--text-xs)] text-faint">Punto de equilibrio</div>
                                        <div className="text-[length:var(--text-xl)] font-semibold tabular-nums text-ink">
                                            {Number.isFinite(result.breakEvenUnits) ? `${Math.ceil(result.breakEvenUnits).toLocaleString('es-AR')} u.` : '—'}
                                        </div>
                                        <div className="text-[length:var(--text-xs)] text-faint">ventas/mes para cubrir fijos</div>
                                    </div>
                                </div>
                                {result.materialBreakdown.length > 0 && (
                                    <div>
                                        <div className="mb-1.5 text-[length:var(--text-xs)] font-medium uppercase tracking-wide text-faint">Desglose de insumos</div>
                                        <ul className="m-0 flex list-none flex-col gap-1 p-0">
                                            {result.materialBreakdown.map((m: any, i: number) => (
                                                <li key={i} className="flex items-baseline justify-between gap-3 border-b border-[var(--border-subtle)] py-1 text-[length:var(--text-sm)] last:border-0">
                                                    <span className="min-w-0 truncate text-soft">
                                                        {m.name}{m.width_mm ? ` (${m.width_mm}×${m.height_mm}mm)` : ''} ×{m.quantity}
                                                    </span>
                                                    <span className="shrink-0 tabular-nums text-ink">{money(m.cost)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                </aside>
            </div>
        </div>
    );
}
